/**
 * C.13a — Dataview Query Language parser + AST stringifier.
 *
 * Grammar implemented (see types.ts header and
 * packages/repancy/research/dataview-bases-scope.md):
 *
 *   TABLE | LIST <columns>?
 *   FROM (#tag | "folder") (AND (#tag | "folder"))*
 *   WHERE <expr>?
 *
 * SORT / LIMIT / GROUP BY are intentionally OMITTED here per the
 * research note (FT-3). The handoff doc TRACK-1-HANDOFF.md claimed
 * those were in scope; the research note overrides since it is the
 * locked spec (Q33).
 *
 * Chevrotain is dynamically imported so it doesn't ship in the
 * synchronous chunk for `/obsidian-vault`. Stringification is pure +
 * synchronous so Bases (C.13b) can reuse it without paying the parser
 * cost.
 */

import type {
  Column,
  ComparisonOp,
  DataviewAst,
  FromAtom,
  FromClause,
  LiteralValue,
  QueryType,
  WhereExpr,
} from './types';

// ============================================================================
// Stringifier — synchronous, no chevrotain. Used by Bases "Export as Dataview".
// ============================================================================

export function stringifyAst(ast: DataviewAst): string {
  const head =
    ast.type === 'LIST'
      ? 'LIST'
      : `TABLE ${ast.columns.map(stringifyColumn).join(', ')}`;
  const from = stringifyFrom(ast.from);
  const where = ast.where ? `WHERE ${stringifyExpr(ast.where)}` : '';
  return [head, from, where].filter(Boolean).join(' ');
}

function stringifyColumn(c: Column): string {
  return c.alias ? `${c.field} as ${c.alias}` : c.field;
}

function stringifyFrom(from: FromClause): string {
  if (from.atoms.length === 0) return '';
  return `FROM ${from.atoms.map(stringifyFromAtom).join(' AND ')}`;
}

function stringifyFromAtom(a: FromAtom): string {
  return a.kind === 'tag' ? `#${a.tag.replace(/^#/, '')}` : `"${a.folder}"`;
}

function stringifyExpr(expr: WhereExpr): string {
  switch (expr.kind) {
    case 'and':
      return `${stringifyExprPart(expr.left, 'and')} AND ${stringifyExprPart(expr.right, 'and')}`;
    case 'or':
      return `${stringifyExprPart(expr.left, 'or')} OR ${stringifyExprPart(expr.right, 'or')}`;
    case 'not':
      return `NOT ${stringifyExprPart(expr.inner, 'not')}`;
    case 'includes':
      return `includes(file.tags, ${stringifyLiteralString(expr.value)})`;
    case 'comparison':
      return `${expr.field} ${expr.op} ${stringifyLiteral(expr.value)}`;
  }
}

function stringifyExprPart(
  inner: WhereExpr,
  parent: 'and' | 'or' | 'not',
): string {
  // Wrap with parens when the inner precedence is looser than the parent.
  const needsParens =
    (parent === 'and' && inner.kind === 'or') ||
    (parent === 'not' && (inner.kind === 'and' || inner.kind === 'or'));
  const s = stringifyExpr(inner);
  return needsParens ? `(${s})` : s;
}

function stringifyLiteral(lit: LiteralValue): string {
  switch (lit.kind) {
    case 'string':
      return stringifyLiteralString(lit.value);
    case 'number':
      return String(lit.value);
    case 'boolean':
      return lit.value ? 'true' : 'false';
    case 'null':
      return 'null';
  }
}

function stringifyLiteralString(s: string): string {
  return `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

// ============================================================================
// Parser — chevrotain, dynamically imported, memoized.
// ============================================================================

import type { ParseError } from './types';

export type ParseResult =
  | { ok: true; ast: DataviewAst }
  | { ok: false; error: ParseError };

let chevrotainInstance: typeof import('chevrotain') | null = null;
let dqlBundle: DqlBundle | null = null;

interface DqlBundle {
  lexer: import('chevrotain').Lexer;
  parser: DqlParser;
  visitor: DqlVisitor;
}

// Need a local interface for the parser/visitor types since they're
// chevrotain-shaped and we import the class lazily.
interface DqlParser {
  input: import('chevrotain').IToken[];
  query: () => import('chevrotain').CstNode;
  errors: { message: string; token?: { startColumn?: number } }[];
}

interface DqlVisitor {
  visit: (cst: import('chevrotain').CstNode) => DataviewAst;
}

export async function compileQuery(source: string): Promise<ParseResult> {
  const bundle = await ensureBundle();
  const lexResult = bundle.lexer.tokenize(source);
  if (lexResult.errors.length > 0) {
    const first = lexResult.errors[0]!;
    return {
      ok: false,
      error: { message: `Lex error: ${first.message}`, column: first.column },
    };
  }
  bundle.parser.input = lexResult.tokens;
  const cst = bundle.parser.query();
  if (bundle.parser.errors.length > 0) {
    const first = bundle.parser.errors[0]!;
    return {
      ok: false,
      error: {
        message: first.message,
        column: first.token?.startColumn,
      },
    };
  }
  try {
    const ast = bundle.visitor.visit(cst);
    return { ok: true, ast };
  } catch (e) {
    return {
      ok: false,
      error: { message: e instanceof Error ? e.message : String(e) },
    };
  }
}

async function ensureBundle(): Promise<DqlBundle> {
  if (dqlBundle) return dqlBundle;
  if (!chevrotainInstance) {
    chevrotainInstance = await import('chevrotain');
  }
  dqlBundle = buildBundle(chevrotainInstance);
  return dqlBundle;
}

function buildBundle(c: typeof import('chevrotain')): DqlBundle {
  const { createToken, Lexer, CstParser } = c;

  // Tokens — order matters: longer matchers first.
  const WhiteSpace = createToken({ name: 'WhiteSpace', pattern: /\s+/, group: Lexer.SKIPPED });
  const Comma = createToken({ name: 'Comma', pattern: /,/ });
  const LParen = createToken({ name: 'LParen', pattern: /\(/ });
  const RParen = createToken({ name: 'RParen', pattern: /\)/ });
  const EQ = createToken({ name: 'EQ', pattern: /==/ });
  const NEQ = createToken({ name: 'NEQ', pattern: /!=/ });
  const LT = createToken({ name: 'LT', pattern: /</ });
  const GT = createToken({ name: 'GT', pattern: />/ });
  const StringLit = createToken({
    name: 'StringLit',
    pattern: /"(?:[^"\\]|\\.)*"/,
  });
  const NumberLit = createToken({
    name: 'NumberLit',
    pattern: /-?\d+(?:\.\d+)?/,
  });
  const TagLit = createToken({
    name: 'TagLit',
    pattern: /#[A-Za-z0-9][A-Za-z0-9_\-/]*/,
  });
  // Keywords — case-insensitive but matched as longest-match against IDENT.
  // We build IDENT after these so keywords win.
  const KwTable = createToken({ name: 'KwTable', pattern: /TABLE/i });
  const KwList = createToken({ name: 'KwList', pattern: /LIST/i });
  const KwFrom = createToken({ name: 'KwFrom', pattern: /FROM/i });
  const KwWhere = createToken({ name: 'KwWhere', pattern: /WHERE/i });
  const KwAnd = createToken({ name: 'KwAnd', pattern: /AND/i });
  const KwOr = createToken({ name: 'KwOr', pattern: /OR/i });
  const KwNot = createToken({ name: 'KwNot', pattern: /NOT/i });
  const KwContains = createToken({ name: 'KwContains', pattern: /contains/i });
  const KwIncludes = createToken({ name: 'KwIncludes', pattern: /includes/i });
  const KwTrue = createToken({ name: 'KwTrue', pattern: /true/i });
  const KwFalse = createToken({ name: 'KwFalse', pattern: /false/i });
  const KwNull = createToken({ name: 'KwNull', pattern: /null/i });
  const KwAs = createToken({ name: 'KwAs', pattern: /\bas\b/i });
  // FT-3 tokens: parsed and tolerated, but the AST drops them. Real
  // Obsidian vaults use SORT/LIMIT/GROUP BY regularly; failing the
  // whole block on encountering them would be hostile UX. Once FT-3
  // lands, these become first-class.
  const KwSort = createToken({ name: 'KwSort', pattern: /SORT/i });
  const KwLimit = createToken({ name: 'KwLimit', pattern: /LIMIT/i });
  const KwGroup = createToken({ name: 'KwGroup', pattern: /GROUP/i });
  const KwBy = createToken({ name: 'KwBy', pattern: /BY/i });
  const KwAsc = createToken({ name: 'KwAsc', pattern: /ASC/i });
  const KwDesc = createToken({ name: 'KwDesc', pattern: /DESC/i });
  const Ident = createToken({ name: 'Ident', pattern: /[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*/ });

  const allTokens = [
    WhiteSpace,
    Comma,
    LParen,
    RParen,
    EQ,
    NEQ,
    LT,
    GT,
    StringLit,
    NumberLit,
    TagLit,
    KwTable,
    KwList,
    KwFrom,
    KwWhere,
    KwAnd,
    KwOr,
    KwNot,
    KwContains,
    KwIncludes,
    KwTrue,
    KwFalse,
    KwNull,
    KwAs,
    KwSort,
    KwLimit,
    KwGroup,
    KwBy,
    KwAsc,
    KwDesc,
    Ident,
  ];

  const lexer = new Lexer(allTokens);

  class DqlParserImpl extends CstParser {
    constructor() {
      super(allTokens, { recoveryEnabled: false });
      this.performSelfAnalysis();
    }

    query = this.RULE('query', () => {
      this.OR([
        { ALT: () => this.SUBRULE(this.tableHead) },
        { ALT: () => this.SUBRULE(this.listHead) },
      ]);
      this.OPTION(() => this.SUBRULE(this.fromClause));
      this.OPTION1(() => this.SUBRULE(this.whereClause));
      // FT-3 tail: SORT / LIMIT / GROUP BY are parsed but ignored.
      // Order is flexible — common Obsidian usage is FROM → WHERE →
      // GROUP BY → SORT → LIMIT.
      this.MANY(() => {
        this.OR1([
          { ALT: () => this.SUBRULE(this.sortClauseIgnored) },
          { ALT: () => this.SUBRULE(this.limitClauseIgnored) },
          { ALT: () => this.SUBRULE(this.groupByClauseIgnored) },
        ]);
      });
    });

    sortClauseIgnored = this.RULE('sortClauseIgnored', () => {
      this.CONSUME(KwSort);
      this.CONSUME(Ident);
      this.OPTION(() =>
        this.OR([
          { ALT: () => this.CONSUME(KwAsc) },
          { ALT: () => this.CONSUME(KwDesc) },
        ]),
      );
      this.MANY(() => {
        this.CONSUME(Comma);
        this.CONSUME2(Ident);
        this.OPTION1(() =>
          this.OR1([
            { ALT: () => this.CONSUME2(KwAsc) },
            { ALT: () => this.CONSUME2(KwDesc) },
          ]),
        );
      });
    });

    limitClauseIgnored = this.RULE('limitClauseIgnored', () => {
      this.CONSUME(KwLimit);
      this.CONSUME(NumberLit);
    });

    groupByClauseIgnored = this.RULE('groupByClauseIgnored', () => {
      this.CONSUME(KwGroup);
      this.CONSUME(KwBy);
      this.CONSUME(Ident);
    });

    tableHead = this.RULE('tableHead', () => {
      this.CONSUME(KwTable);
      this.OPTION(() => this.SUBRULE(this.columnList));
    });

    listHead = this.RULE('listHead', () => {
      this.CONSUME(KwList);
    });

    columnList = this.RULE('columnList', () => {
      this.SUBRULE(this.column);
      this.MANY(() => {
        this.CONSUME(Comma);
        this.SUBRULE2(this.column);
      });
    });

    column = this.RULE('column', () => {
      this.CONSUME(Ident);
      this.OPTION(() => {
        this.CONSUME(KwAs);
        this.CONSUME2(Ident);
      });
    });

    fromClause = this.RULE('fromClause', () => {
      this.CONSUME(KwFrom);
      this.SUBRULE(this.fromAtom);
      this.MANY(() => {
        this.CONSUME(KwAnd);
        this.SUBRULE2(this.fromAtom);
      });
    });

    fromAtom = this.RULE('fromAtom', () => {
      this.OR([
        { ALT: () => this.CONSUME(TagLit) },
        { ALT: () => this.CONSUME(StringLit) },
      ]);
    });

    whereClause = this.RULE('whereClause', () => {
      this.CONSUME(KwWhere);
      this.SUBRULE(this.orExpr);
    });

    orExpr = this.RULE('orExpr', () => {
      this.SUBRULE(this.andExpr);
      this.MANY(() => {
        this.CONSUME(KwOr);
        this.SUBRULE2(this.andExpr);
      });
    });

    andExpr = this.RULE('andExpr', () => {
      this.SUBRULE(this.notExpr);
      this.MANY(() => {
        this.CONSUME(KwAnd);
        this.SUBRULE2(this.notExpr);
      });
    });

    notExpr = this.RULE('notExpr', () => {
      this.OPTION(() => this.CONSUME(KwNot));
      this.SUBRULE(this.atomExpr);
    });

    atomExpr = this.RULE('atomExpr', () => {
      this.OR([
        { ALT: () => this.SUBRULE(this.parenExpr) },
        { ALT: () => this.SUBRULE(this.includesExpr) },
        { ALT: () => this.SUBRULE(this.comparisonExpr) },
      ]);
    });

    parenExpr = this.RULE('parenExpr', () => {
      this.CONSUME(LParen);
      this.SUBRULE(this.orExpr);
      this.CONSUME(RParen);
    });

    includesExpr = this.RULE('includesExpr', () => {
      this.CONSUME(KwIncludes);
      this.CONSUME(LParen);
      this.CONSUME(Ident);
      this.CONSUME(Comma);
      this.CONSUME(StringLit);
      this.CONSUME(RParen);
    });

    comparisonExpr = this.RULE('comparisonExpr', () => {
      this.CONSUME(Ident);
      this.OR([
        { ALT: () => this.CONSUME(EQ) },
        { ALT: () => this.CONSUME(NEQ) },
        { ALT: () => this.CONSUME(LT) },
        { ALT: () => this.CONSUME(GT) },
        { ALT: () => this.CONSUME(KwContains) },
      ]);
      this.SUBRULE(this.literal);
    });

    literal = this.RULE('literal', () => {
      this.OR([
        { ALT: () => this.CONSUME(StringLit) },
        { ALT: () => this.CONSUME(NumberLit) },
        { ALT: () => this.CONSUME(KwTrue) },
        { ALT: () => this.CONSUME(KwFalse) },
        { ALT: () => this.CONSUME(KwNull) },
      ]);
    });
  }

  const parser = new DqlParserImpl();

  // CST → AST visitor.
  class DqlVisitorImpl extends parser.getBaseCstVisitorConstructorWithDefaults() {
    constructor() {
      super();
      this.validateVisitor();
    }

    query(ctx: Record<string, unknown[]>): DataviewAst {
      const type: QueryType = ctx['tableHead'] ? 'TABLE' : 'LIST';
      const columns: Column[] =
        ctx['tableHead']
          ? (this.visit(ctx['tableHead'] as never) as Column[])
          : [];
      const from: FromClause = ctx['fromClause']
        ? (this.visit(ctx['fromClause'] as never) as FromClause)
        : { atoms: [] };
      const where = ctx['whereClause']
        ? (this.visit(ctx['whereClause'] as never) as WhereExpr)
        : undefined;
      return { type, columns, from, where };
    }

    tableHead(ctx: Record<string, unknown[]>): Column[] {
      if (!ctx['columnList']) return [];
      return this.visit(ctx['columnList'] as never) as Column[];
    }

    listHead(): null {
      return null;
    }

    // FT-3 stubs — recognized + ignored.
    sortClauseIgnored(): null {
      return null;
    }
    limitClauseIgnored(): null {
      return null;
    }
    groupByClauseIgnored(): null {
      return null;
    }

    columnList(ctx: Record<string, unknown[]>): Column[] {
      const cols = ctx['column'] as unknown[];
      return cols.map((c) => this.visit(c as never) as Column);
    }

    column(ctx: { Ident: { image: string }[] }): Column {
      const idents = ctx.Ident;
      if (idents.length === 1) {
        return { field: idents[0]!.image };
      }
      return { field: idents[0]!.image, alias: idents[1]!.image };
    }

    fromClause(ctx: Record<string, unknown[]>): FromClause {
      const atomsCtx = ctx['fromAtom'] as unknown[];
      const atoms = atomsCtx.map((a) => this.visit(a as never) as FromAtom);
      return { atoms };
    }

    fromAtom(ctx: { TagLit?: { image: string }[]; StringLit?: { image: string }[] }): FromAtom {
      if (ctx.TagLit && ctx.TagLit.length > 0) {
        return { kind: 'tag', tag: ctx.TagLit[0]!.image };
      }
      const raw = ctx.StringLit![0]!.image;
      return { kind: 'folder', folder: unquote(raw) };
    }

    whereClause(ctx: Record<string, unknown[]>): WhereExpr {
      return this.visit(ctx['orExpr'] as never) as WhereExpr;
    }

    orExpr(ctx: Record<string, unknown[]>): WhereExpr {
      const ands = ctx['andExpr'] as unknown[];
      let left = this.visit(ands[0] as never) as WhereExpr;
      for (let i = 1; i < ands.length; i++) {
        const right = this.visit(ands[i] as never) as WhereExpr;
        left = { kind: 'or', left, right };
      }
      return left;
    }

    andExpr(ctx: Record<string, unknown[]>): WhereExpr {
      const nots = ctx['notExpr'] as unknown[];
      let left = this.visit(nots[0] as never) as WhereExpr;
      for (let i = 1; i < nots.length; i++) {
        const right = this.visit(nots[i] as never) as WhereExpr;
        left = { kind: 'and', left, right };
      }
      return left;
    }

    notExpr(ctx: { KwNot?: unknown[]; atomExpr: unknown[] }): WhereExpr {
      const inner = this.visit(ctx.atomExpr[0] as never) as WhereExpr;
      if (ctx.KwNot && ctx.KwNot.length > 0) {
        return { kind: 'not', inner };
      }
      return inner;
    }

    atomExpr(ctx: Record<string, unknown[]>): WhereExpr {
      if (ctx['parenExpr']) return this.visit(ctx['parenExpr'] as never) as WhereExpr;
      if (ctx['includesExpr']) return this.visit(ctx['includesExpr'] as never) as WhereExpr;
      return this.visit(ctx['comparisonExpr'] as never) as WhereExpr;
    }

    parenExpr(ctx: Record<string, unknown[]>): WhereExpr {
      return this.visit(ctx['orExpr'] as never) as WhereExpr;
    }

    includesExpr(ctx: { Ident: { image: string }[]; StringLit: { image: string }[] }): WhereExpr {
      const fieldName = ctx.Ident[0]!.image;
      if (fieldName !== 'file.tags') {
        throw new Error(
          `includes() expects 'file.tags' as the first arg, got '${fieldName}'`,
        );
      }
      return { kind: 'includes', field: 'file.tags', value: unquote(ctx.StringLit[0]!.image) };
    }

    comparisonExpr(ctx: {
      Ident: { image: string }[];
      EQ?: unknown[];
      NEQ?: unknown[];
      LT?: unknown[];
      GT?: unknown[];
      KwContains?: unknown[];
      literal: unknown[];
    }): WhereExpr {
      const field = ctx.Ident[0]!.image;
      let op: ComparisonOp;
      if (ctx.EQ) op = '==';
      else if (ctx.NEQ) op = '!=';
      else if (ctx.LT) op = '<';
      else if (ctx.GT) op = '>';
      else op = 'contains';
      const value = this.visit(ctx.literal[0] as never) as LiteralValue;
      return { kind: 'comparison', field, op, value };
    }

    literal(ctx: {
      StringLit?: { image: string }[];
      NumberLit?: { image: string }[];
      KwTrue?: unknown[];
      KwFalse?: unknown[];
      KwNull?: unknown[];
    }): LiteralValue {
      if (ctx.StringLit) return { kind: 'string', value: unquote(ctx.StringLit[0]!.image) };
      if (ctx.NumberLit) return { kind: 'number', value: Number(ctx.NumberLit[0]!.image) };
      if (ctx.KwTrue) return { kind: 'boolean', value: true };
      if (ctx.KwFalse) return { kind: 'boolean', value: false };
      return { kind: 'null' };
    }
  }

  const visitor = new DqlVisitorImpl();
  return { lexer, parser: parser as unknown as DqlParser, visitor: visitor as unknown as DqlVisitor };
}

function unquote(s: string): string {
  // s includes the surrounding quotes per chevrotain token shape.
  return s.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, '\\');
}
